<script>
/**
 * The Scene class acts as the container instance to support the 
 * TBS Scene plugin for the scene view. It introduces the 
 * scene-based development pattern for the whole application
 * or scoped view.
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import Vue from 'vue'
import { Dialog, Toast, NavBar } from 'vant'
import SceneBridge from '@/tbs/core/SceneBridge'
import { AppType } from '@/utils'
import { goLogin, setTitle } from '@/libs/ds-bridge'
import GlobalFunc from '@/tbs/util/GlobalFunc'
import ClassFunc from '@/tbs/util/ClassFunc'
import ModelLocator from '@/tbs/model/ModelLocator'

//--------------------------------------------------
// global registration
//--------------------------------------------------
Vue.use(Dialog, Toast)

//--------------------------------------------------
// private properties
//--------------------------------------------------
let bridge = null
let mediator = null
let convertor = null
let translator = null
let ignoreUpdateOnce = false

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-nav-bar': NavBar
    },

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
	data() {
		return {
            /* The nav bar title string*/
            navBarTitle: null,
            showNavBar: true
		}
	},

    //--------------------------------------------------
    // component life cycle trigger
    //--------------------------------------------------
    /**
     * @created
     * It presents the TBS Scene plugin for the vue.
     */
    created() {
        console.log('TBS Scene plugin for the vue is initialized.')
        this.init()
    },
    /**
     * @updated
     * Listen the updated trigger for the children components.
     * It works for the usual life cycle after mounted.
     */
    updated() {
        // console.log('@updated', ignoreUpdateOnce)
        if (ignoreUpdateOnce) {
            ignoreUpdateOnce = false
            return
        }
        this.creationComplete()
    },
    /**
     * @mounted
     * Listen the mounted trigger for the children components.
     * It works for the first time while mounting the real scene view instance.
     */
    mounted() {
        // console.log('@mounted')
        // set the native title empty to hide the navbar within app
        setTitle("")
        // trigger the creation complete handler to initialize the DOM related elements
        this.creationComplete()
    },
    /**
     * @beforeRouteUpdate
     * Listen the route update  trigger for the route changed.
     * Try to destroy the last scene-related instance before next round.
     */
    beforeRouteUpdate(to, from, next) {
        // console.log('beforeRouteUpdate', to, from)
        // cached condition
        const meta = this.$route.meta
        // avoid the null value of the meta properties object
        meta.properties = meta.properties || {}
        // validate the cached flag within the meta properties
        if (meta.properties.cached) {
            const cached = meta.properties.cached
            // prefetch the route name as the status id
            const id = this.$route.name
            // prefetch the scene view as the view object
            const view = this.$refs.sceneView
            // validate the cached class type
            // if existed, set the status for the view slot
            if (ClassFunc.getClass(cached) == 'Boolean') {
                // if cached is Boolean, use the convertor.name as the slot.
                // prefetch the name slot via convertor instance
                const name = convertor.name
                // save the cached data into the status within the id
                ModelLocator.setStatus(id, view[name])
            }
            else if (ClassFunc.getClass(cached) == 'String') {
                // if cached is String, use the defined slots.
                const cachedArr = cached.split(',')
                const statusObj = {}
                for (let i = 0; i < cachedArr.length; i++) {
                    let cachedName = cachedArr[i]
                    statusObj[cachedName] = view[cachedName]
                }
                // save the cached data into the status within the id
                ModelLocator.setStatus(id, statusObj)
            }
        }
        // trigger the destroy method if existed
        mediator && mediator.destroy()
        // clear the mediator instance
        mediator = null
        // clear the convertor instance
        convertor = null
        // clear the translator instance
        translator = null
        // go on after that
        next()
    },

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        /**
         * @getter
         * Get the nav bar title label.
         */
        title() {
            return this.navBarTitle ? this.navBarTitle : ''
        }
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        updateTitle(title) {
            console.log('@updateTitle', ignoreUpdateOnce, title)
            ignoreUpdateOnce = true
            if (this.navBarTitle != title) {
                this.navBarTitle = title
            }
        },
        /**
         * @handler
         * Handle the back button click event.
         */
        onBackBtnClick() {
            if (mediator && mediator['handleRouteBack']) {
                mediator.handleRouteBack()
            }
            else {
                // history back
                this.$router.go(-1)
            }
        },
        /**
         * @handler
         * Handle the custom event and passed into the mediator.
         */
        onCustomEvent(event) {
            // console.log('onCustomEvent', event)
            // append the target instance via the scene view
            event.target = this.$refs.sceneView
            if (mediator && mediator['handleCustomEvent']) {
                mediator.handleCustomEvent(event)
            }
        },

        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * The scene view initialize the bridge, mediator, convertor, translator
         * and so on where creation complete.
         */
        creationComplete() {
            // console.log('Scene.creationComplete')
            // create the scene bridge instance anyway
            // const bridge = new SceneBridge(this)
            // create the scene bridge once
            if (bridge == null) {
                bridge = new SceneBridge(this)
            }
            // console.log('@bridge', bridge)
            // refer the route meta
            const meta = this.$route.meta
            // console.log('@creationComplete', meta)
            // avoid the null value of the meta properties object
            meta.properties = meta.properties || {}
            // console.log('before parse title', meta.properties.title, meta.properties.query)
            const title = this.parseTitle(meta.properties.title, meta.properties.query)
             // console.log('@title', this.navBarTitle, meta.properties.title, this.navBarTitle == meta.properties.title)
            // validate the title property
            if (this.navBarTitle == title) {
                // ignore the updated hook caused by the title update
                return
            }
            else {
                // set the nav bar title via title property within the meta
                this.navBarTitle = title
            }

            // validate the passport property
            if (meta.properties.login == 'app') {
                // console.log('@login app', this.$route.query.passport, GlobalFunc.getCookie('passport'))
                if (!this.$route.query.passport && !GlobalFunc.getCookie('passport')) {
                    console.log('no passport condition, then go login page')
                    return goLogin()
                }
                else if (this.$route.query.passport && !GlobalFunc.getCookie('passport')) {
                    GlobalFunc.setCookie('passport', this.$route.query.passport)
                }
            }
            // validate the hideNavBar property
            if (meta.properties.hideNavBar) {
                // hide the nav bar via hideNavBar property within the meta
                this.showNavBar = false
            }
            else {
                // show the nav bar if title existed
                this.showNavBar = true
            }

            // validate the mediator
            if (meta.mediator) {
                // load the mediator
                meta.mediator()
                    .then(module => {
                        // console.log('mediator loader success', module)
                        mediator = new module.default(bridge)
                        this.validateBundles()
                    })
                    .catch(err => {
                        console.log('mediator loader error', err)
                    })
            }
            // validate the convertor
            if (meta.convertor) {
                // load the convertor
                meta.convertor()
                    .then(module => {
                         // console.log('convertor loader success', module)
                        convertor = new module.default
                        this.validateBundles()
                    })
                    .catch(err => {
                        console.log('convertor loader error', err)
                    })
            }
            // validate the translator
            if (meta.translator) {
                // load the translator
                meta.translator()
                    .then(module => {
                        // console.log('translator loader success', module)
                        translator = new module.default
                        this.validateBundles()
                    })
                    .catch(err => {
                        console.log('translator loader error', err)
                    })
            }
        },
        /**
         * @private
         * Parse the title string according to the properties title and query.
         * If query existed, replace the query property within the title string via route query.
         * @param propertiesTitle The properties title
         * @param propertiesQuery The properties query
         * @return The parsed title
         */
        parseTitle(propertiesTitle, propertiesQuery) {
            // console.log('@parseTitle', propertiesTitle, propertiesQuery)
            let title = propertiesTitle
            if (!title) {
                return
            }
            if (!propertiesQuery) {
                return title
            }
            const queryArr = propertiesQuery.split(',')
            const routeQuery = this.$route.query
            for (let i = 0; i < queryArr.length; i++) {
                let item = queryArr[i]
                let queryVal = routeQuery[item]
                if (queryVal) {
                    title = title.replaceAll(item, queryVal)
                }
            }
            return title
        },
        /**
         * @private
         * Validate the dynamic module loaded complete or not.
         * If completed, initialize the mediator instance.
         */
        validateBundles() {
            // validate all module loaded complete
            if (mediator && convertor && translator && this.$refs.sceneView) {
                // console.log('validateBundles passed')
                // append the translator object to the locale one within the scene view
                this.$refs.sceneView.locale = translator
                // set the target for the scene mediator
                mediator.target(this.$refs.sceneView)
                // set the convertor for the scene mediator
                mediator.convertor(convertor)
                // set the translator for the scene mediator
                mediator.translator(translator)
                // prefetch the properties
                const properties = this.$route.meta.properties
                // set the properties via the properies from the route meta
                mediator.properties(properties)
                // validate the cached
                if (properties.cached) {
                    // prefetch the route name as the status id
                    const id = this.$route.name
                    // prefetch the scene view as the view object
                    const view = this.$refs.sceneView
                    // try to prefetch the status according to the status id
                    const status = ModelLocator.getStatus(id)
                    // validate the status
                    if (status) {
                        // if existed, set the status for the view slot
                        if (ClassFunc.getClass(properties.cached) == 'Boolean') {
                            // if cached is Boolean, use the convertor.name as the slot.
                            // prefetch the name slot via convertor instance
                            const name = convertor.name
                            view[name] = status
                        }
                        else if (ClassFunc.getClass(properties.cached) == 'String') {
                            // if cached is String, use the defined slots.
                            const cachedArr = properties.cached.split(',')
                            for (let i = 0; i < cachedArr.length; i++) {
                                let cachedName = cachedArr[i]
                                view[cachedName] = status[cachedName]
                            }
                        }
                        // return
                        return
                    }           
                    // if not existed, continue the startup workflow
                }
                // trigger the handler for the creation complete in the mediator
                mediator.onCreationComplete()
            }
        },
        /**
         * @private
         * Init the scene environment globally.
         */
        init() {
            // @compatible-code Try to fix the page offset issue while input via soft keyboard on iOS.
            if (AppType.isInIOS) {
                let scrollY = window.scrollY
                document.addEventListener('focusin', function() {
                    scrollY = window.scrollY
                })
                document.addEventListener('focusout', function() {
                    setTimeout(function() {
                        window.scrollTo({
                            top: scrollY,
                            behavior: "smooth"
                        });
                    }, 100)
                })
            }
        }
    }
}
</script>

<template>
    <!-- component -->
    <div class="scene">
        <!-- nav bar -->
        <div class="header" v-if="showNavBar">
            <van-nav-bar 
                :title="title"
                :fixed="true"
                :safe-area-inset-top="true"
                class="nav-bar"
                left-arrow  
                @click-left="onBackBtnClick"
            />
        </div>
        <!-- router view as scene view -->
        <router-view
            class="scene-view"
            :key="Date.now()" 
            ref="sceneView" 
            @custom-event="onCustomEvent" 
        />
    </div>
</template>

<style src="../stylesheet/common.css"/>
<style src="../stylesheet/iconfont.css"/>